import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Table,
  Modal,
  Alert
} from 'react-bootstrap';

const Translations = () => {
  const [collections, setCollections] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [newCollection, setNewCollection] = useState('');
  const [newTranslation, setNewTranslation] = useState({
    fieldKey: '',
    content: '',
    language: 'en_US'
  });
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const languages = [
    { code: 'en_US', name: 'English (USA)' },
    { code: 'ru_RU', name: 'Russian' },
    // Add more languages as needed
  ];

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const response = await axios.get('/api/translations/collections');
      setCollections(response.data.data);
    } catch (error) {
      setError('Failed to fetch collections');
    }
  };

  const fetchTranslations = async (collectionName) => {
    try {
      const response = await axios.get(`/api/translations/collections/${collectionName}/translations`);
      setTranslations(response.data.data);
    } catch (error) {
      setError('Failed to fetch translations');
    }
  };

  const handleCollectionSelect = (collectionName) => {
    setSelectedCollection(collectionName);
    fetchTranslations(collectionName);
  };

  const handleCreateCollection = async () => {
    try {
      await axios.post('/api/translations/collections', {
        name: newCollection,
        servers: [],
        blacklist: true
      });
      setNewCollection('');
      fetchCollections();
      setSuccess('Collection created successfully');
    } catch (error) {
      setError('Failed to create collection');
    }
  };

  const handleCreateTranslation = async () => {
    try {
      await axios.post('/api/translations/translations', {
        collection: selectedCollection,
        fieldKey: newTranslation.fieldKey,
        content: {
          [newTranslation.language]: newTranslation.content
        }
      });
      setNewTranslation({ fieldKey: '', content: '', language: 'en_US' });
      setShowModal(false);
      fetchTranslations(selectedCollection);
      setSuccess('Translation created successfully');
    } catch (error) {
      setError('Failed to create translation');
    }
  };

  const handleUpdateTranslation = async (twinId, content) => {
    try {
      await axios.put(`/api/translations/translations/${twinId}`, {
        content: JSON.stringify(content)
      });
      fetchTranslations(selectedCollection);
      setSuccess('Translation updated successfully');
    } catch (error) {
      setError('Failed to update translation');
    }
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>Translations Manager</h2>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Collections</Form.Label>
            <Form.Control
              as="select"
              value={selectedCollection}
              onChange={(e) => handleCollectionSelect(e.target.value)}
            >
              <option value="">Select a collection</option>
              {collections.map((collection) => (
                <option key={collection.name} value={collection.name}>
                  {collection.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Create New Collection</Form.Label>
            <Form.Control
              type="text"
              value={newCollection}
              onChange={(e) => setNewCollection(e.target.value)}
              placeholder="Enter collection name"
            />
            <Button
              variant="primary"
              className="mt-2"
              onClick={handleCreateCollection}
              disabled={!newCollection}
            >
              Create Collection
            </Button>
          </Form.Group>
        </Col>
      </Row>

      {selectedCollection && (
        <>
          <Row className="mb-4">
            <Col>
              <Button
                variant="primary"
                onClick={() => setShowModal(true)}
              >
                Add Translation
              </Button>
            </Col>
          </Row>

          <Row>
            <Col>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Field Key</th>
                    {languages.map((lang) => (
                      <th key={lang.code}>{lang.name}</th>
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {translations.map((translation) => (
                    <tr key={translation.twin_id}>
                      <td>{translation.field_key}</td>
                      {languages.map((lang) => (
                        <td key={lang.code}>
                          {JSON.parse(translation.content)[lang.code] || ''}
                        </td>
                      ))}
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="mr-2"
                          onClick={() => {
                            // Implement edit functionality
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            // Implement delete functionality
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>
        </>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Translation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Field Key</Form.Label>
              <Form.Control
                type="text"
                value={newTranslation.fieldKey}
                onChange={(e) =>
                  setNewTranslation({ ...newTranslation, fieldKey: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Language</Form.Label>
              <Form.Control
                as="select"
                value={newTranslation.language}
                onChange={(e) =>
                  setNewTranslation({ ...newTranslation, language: e.target.value })
                }
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group>
              <Form.Label>Content</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newTranslation.content}
                onChange={(e) =>
                  setNewTranslation({ ...newTranslation, content: e.target.value })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateTranslation}
            disabled={!newTranslation.fieldKey || !newTranslation.content}
          >
            Create Translation
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Translations; 